import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { AppContext } from '@prisjakt/ui-app-context';
import { useSelector } from 'react-redux';
import SecondarySearchField from '@prisjakt/ui-secondary-search-field';
import { AggregationSection } from '../../../../types';
import { SIDEBAR_STATE } from '../../../sidebar/constants';
import { FILTER_PORTAL_ID } from '../../constants';
import FilterSection, {
  isValidSection,
  findUngroupedSections,
  findGroupedSections,
  SupportedSection,
  SectionGroup,
} from '../filter-section';
import * as S from './styles';
import FilterSectionGroup from './group';
import { ProductCollectionNonNameSearchFilter } from './types';

type Props = {
  sections: AggregationSection[];
  filters: ProductCollectionNonNameSearchFilter[];
};

const SidebarFilter = ({ sections, filters }: Props) => {
  const { t, market } = React.useContext(AppContext);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>();
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const sidebarTab = useSelector((state: any) => state.areas.sidebar.tab);

  const [sectionTitleFilter, setSectionTitleFilter] = useState<string>();
  const isFilteringSections =
    typeof sectionTitleFilter === 'string' && sectionTitleFilter.length > 1;
  const locale = market.locale.replace('_', '-');
  const hasSectionMatchingFilter = (group: SectionGroup) =>
    group.sections.some(matchesSectionFilter);
  const matchesSectionFilter = (section: SupportedSection) =>
    !isFilteringSections ||
    section.title
      .toLocaleLowerCase(locale)
      .includes(sectionTitleFilter?.toLocaleLowerCase(locale) || '');

  const toggleExpandedGroup = useCallback(
    (id: string) => {
      if (expandedGroups.includes(id)) {
        setExpandedGroups(expandedGroups.filter(groupId => groupId !== id));
      } else {
        setExpandedGroups([...expandedGroups, id]);
      }
    },
    [expandedGroups]
  );

  const ungroupedSections = useMemo(() => findUngroupedSections(sections), [
    sections,
  ]);

  const sectionGroups = useMemo(() => findGroupedSections(sections, filters), [
    sections,
    filters,
  ]);

  /**
   * The whole filter slice is rendered through a portal in order to keep it
   * within the route data structure while still being able to show it in the
   * sidebar. We have to do this little dance with local state and an effect
   * to make sure the portal target has been rendered before trying to render
   * into it.
   */
  useEffect(() => {
    if (sidebarTab === SIDEBAR_STATE.FILTER) {
      setTimeout(() => {
        setPortalTarget(document.getElementById(FILTER_PORTAL_ID));
      }, 0);
    }
  }, [sidebarTab]);

  if (!portalTarget) {
    return null;
  }

  return ReactDOM.createPortal(
    <S.Filter>
      <S.FilterTitle>{t('filter', 'filter.title')}</S.FilterTitle>

      <S.FilterSearchWrapper>
        <SecondarySearchField
          value={sectionTitleFilter}
          onChange={setSectionTitleFilter}
          clearText={t('common', 'button.clear')}
          placeholder={t('filter', 'properties.search-placeholder')}
        />
      </S.FilterSearchWrapper>

      {sections && sections.some(isValidSection) ? (
        <>
          {ungroupedSections.length > 0 && (
            <S.SectionList>
              {ungroupedSections.filter(matchesSectionFilter).map(section => (
                <S.SectionListItem key={section.id}>
                  <FilterSection section={section} showTitle />
                </S.SectionListItem>
              ))}
            </S.SectionList>
          )}
          {sectionGroups.length > 0 && (
            <S.SectionList>
              {sectionGroups.filter(hasSectionMatchingFilter).map(group => (
                <S.SectionListItem noPadding key={group.title}>
                  <FilterSectionGroup
                    group={group}
                    expanded={
                      expandedGroups.includes(group.title) ||
                      isFilteringSections
                    }
                    toggleExpanded={toggleExpandedGroup}
                    dark
                  >
                    <S.SectionList>
                      {group.sections
                        .filter(matchesSectionFilter)
                        .map(section => (
                          <S.SectionListItem key={section.id}>
                            <FilterSection section={section} showTitle />
                          </S.SectionListItem>
                        ))}
                    </S.SectionList>
                  </FilterSectionGroup>
                </S.SectionListItem>
              ))}
            </S.SectionList>
          )}
        </>
      ) : (
        <S.NoContent>{t('filter', 'filter.no-advanced-filters')}</S.NoContent>
      )}
    </S.Filter>,
    portalTarget
  );
};

export default SidebarFilter;
